import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Sprite, Graphics, Container, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import { getNewUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { useFreeSpinBasicControl } from "../../../../Contexts/FreeSpinBasicControlContext";
import {
  selectFreeSpinsRemainingDisplay,
  selectFreeSpinsAwaitingResume,
  selectFreeSpinsReloadPopupPending,
  selectFreeSpinsSpinning,
} from "../../../../State/selectors";

/**
 * FreeSpinButtonPlay — the play button during the Mad Labs free spins round.
 *
 * IT NEVER STARTS A SPIN. The round runs itself; the only thing this button
 * does is land the current spin sooner. That is the same job
 * ButtonPotCollectionPlay has, and this is built on its shape rather than on
 * the base ButtonPlay's.
 *
 * It was a 1,706-line copy of the base play button, carrying spacebar
 * handling, hold-to-turbo, autoplay interplay, queued stops during windup,
 * skip-win-animations, rate limiting and a pending-stop store subscription.
 * A round the player does not drive needs none of it, and every one of those
 * paths was a way for the button to do something the round did not ask for.
 *
 * Spacebar is NOT handled here. FreeSpinBasicControlContext owns it for the
 * whole round, so there is exactly one listener and it does exactly one thing.
 * The base ButtonPlay's own listener is gone with it — the toolbar swaps this
 * button in for that one, so it is unmounted.
 *
 * The number on the face is the spins remaining AS ANNOUNCED. A level-up's new
 * spins are held back until the popup says them and the room re-dresses — see
 * selectFreeSpinsRemainingDisplay. Reading the raw count made the button climb
 * the instant the response landed, seconds before anything told the player why.
 * Pot Collection drives its counter imperatively through a ref because its
 * respin counter controller owns the value; ours is just state, so it does
 * not need the indirection.
 */

// Position and size — IDENTICAL to secondary ButtonPlay, so the button does
// not move when the toolbar swaps one for the other mid-round.
const PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 1.9 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.45 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 2.175 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.375 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 2.25 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.675 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { deviceMultiplier: 1.0 },
  },
};

const FreeSpinButtonPlay = ({ toolbar }) => {
  const { t } = useTranslation();
  const control = useFreeSpinBasicControl();

  const remaining = useSelector(selectFreeSpinsRemainingDisplay);
  const spinning = useSelector(selectFreeSpinsSpinning);
  // A reloaded round is parked until the player presses. While it is, this
  // button is the only way forward, so it pulses to say so.
  const awaitingResume = useSelector(selectFreeSpinsAwaitingResume);
  // The reload popup still owns the screen. Pot Collection's autoplay gate —
  // where its pulse lives — hard-blocks on this, so nothing glows and nothing
  // is pressable until the popup is gone.
  const reloadPopupPending = useSelector(selectFreeSpinsReloadPopupPending);
  const winAnimationsPlaying = useSelector(
    (state) => state.animations.winAnimationsPlaying,
  );

  // ── Ready to be pressed ──
  //
  // The glow means exactly this: press me, by click, tap or spacebar, and the
  // round starts. It must not promise that while the popup is still up.
  const canResume = awaitingResume && !reloadPopupPending;

  // [FSResume] TEMP DIAGNOSTIC — remove once the press is proven.
  useEffect(() => {
  }, [spinning, awaitingResume, reloadPopupPending, canResume, control]);

  const {
    dimensions,
    uiSymbolSize,
    device,
    orientation,
    getResponsiveConfig,
    isMobileSmall,
  } = useDimensions();

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // ── The SHARED play-button ref, not a local one ──
  //
  // Every play button in the game puts its sprite here — base ButtonPlay,
  // ButtonPotCollectionPlay, ButtonHoldAndWinPlay — because only one of them is
  // ever mounted at a time (ToolbarBottom picks exactly one) and the CONTEXTS
  // animate the button through it: ReelControlContext rotates it on every base
  // spin, PotCollectionControlContext both rotates and pulses it.
  //
  // A local ref here is what left this button unable to be driven by its own
  // context — no spin rotation, and a glow that had to be hand-rolled in the
  // component instead of living at the gate that owns it.
  const { playButtonRef: spriteRef } = useReelRefs();

  // Hover brightness — the secondary theme's pattern, and the only cosmetic
  // behaviour kept from the original.
  const brightnessFilter = useRef(null);
  if (!brightnessFilter.current) {
    brightnessFilter.current = new PIXI.ColorMatrixFilter();
    brightnessFilter.current.brightness(1.6, false);
    brightnessFilter.current.resolution = window.devicePixelRatio || 1;
    brightnessFilter.current.padding = 8;
  }

  const handlePointerOver = useCallback(() => {
    const sprite = spriteRef.current;
    if (sprite && (!sprite.filters || sprite.filters.length === 0)) {
      sprite.filters = [brightnessFilter.current];
    }
  }, [spriteRef]);

  const handlePointerOut = useCallback(() => {
    if (spriteRef.current) spriteRef.current.filters = [];
  }, [spriteRef]);

  /**
   * Two actions, and which one applies is never ambiguous.
   *
   *   canResume       a reloaded round is parked and the popup is gone — this
   *                   press starts it. Nothing is spinning, so nothing to stop.
   *   spinning        land the reels sooner. The only action a running round has.
   *
   * A tap at any other moment does nothing: it must not be able to start a spin
   * or queue one for later.
   */
  // The two moments with an action behind them — see the hit area below.
  // A celebration counts too: ButtonPlay's resolveButtonAction ranks
  // SKIP_ANIMATIONS above STOP_SPIN, so the press has an action there as well
  // and the hit area must be live for it.
  const isInteractive = spinning || canResume || winAnimationsPlaying;

  const handleTap = useCallback(() => {
    // One resolver for both inputs, and it lives in the context so the button
    // and the spacebar can never disagree — see handlePress.
    control?.handlePress?.();
  }, [control]);

  // The waiting glow is NOT here. It lives in FreeSpinBasicControlContext, at
  // the loop gate that parks the round — exactly where Pot Collection keeps its
  // own pulse, and on the same shared sprite. See handleResume, which retires it
  // on the press the way startAutoplay does.

  const buttonPosition = useMemo(() => {
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y:
            -toolbar.visualHeight -
            buttonConfig.height / 2 -
            buttonConfig.height * 0.075,
        };
      }
      return {
        x: toolbar.width - buttonConfig.width * 0.55,
        y: -dimensions.height / 2 + buttonConfig.height / 1.8,
      };
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y:
            -toolbar.visualHeight -
            buttonConfig.height / 2 -
            buttonConfig.height * 0.065,
        };
      }
      return {
        x: toolbar.width - buttonConfig.width * 0.415,
        y: -dimensions.height / 2 + buttonConfig.height / 1.8,
      };
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return { x: toolbar.width / 2, y: -dimensions.height / 5.0 };
      }
      return {
        x: toolbar.width - buttonConfig.width / 1.4,
        y: toolbar.height - buttonConfig.height / 1.7,
      };
    }
    return {
      x: toolbar.width - buttonConfig.width * 1.285,
      y: uiSymbolSize * 0.08,
    };
  }, [
    device,
    orientation,
    isMobileSmall,
    dimensions.height,
    toolbar.width,
    toolbar.height,
    toolbar.visualHeight,
    buttonConfig.width,
    buttonConfig.height,
    uiSymbolSize,
  ]);

  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width],
  );

  // Same face and sizing as the Pot Collection counter, so the two features
  // read as one game.
  const counterTextStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: '"Noto Sans", sans-serif',
        fontSize: Math.round(buttonConfig.width * 0.32),
        fontWeight: "800",
        fill: 0xffffff,
        align: "center",
      }),
    [buttonConfig.width],
  );

  const iconTexture = getNewUITexture("icon_play");

  return (
    <Container
      name="secondary_free_spin_button_play"
      x={buttonPosition.x}
      y={buttonPosition.y}
    >
      {/* Interactive area — live for exactly the two moments that HAVE an
          action, so the button can never be pressed into doing nothing:
          while a spin is in flight (land it sooner), and while a reloaded
          round is parked (resume it). `spinning` alone left the hit area
          dead on the reload screen, where nothing is spinning — the glow
          pulsed and the press did nothing. */}
      <Graphics
        name="free_spin_button_play_interactive_area"
        eventMode={isInteractive ? "static" : "none"}
        cursor={isInteractive ? "pointer" : "default"}
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        onpointertap={handleTap}
        draw={(g) => {
          g.clear();
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
        accessible={true}
        accessibleTitle={
          spinning
            ? t("stop_button", { ns: "common" })
            : t("play_button", { ns: "common" })
        }
      />

      <Sprite
        ref={spriteRef}
        texture={iconTexture}
        name="free_spin_button_play_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        roundPixels={true}
        x={0}
        y={0}
        eventMode="none"
        resolution={2}
      />

      {/* Spins remaining, on the face of the button. */}
      <Text
        text={String(remaining)}
        style={counterTextStyle}
        anchor={0.5}
        x={0}
        y={0}
        zIndex={1}
        eventMode="none"
        roundPixels={true}
        resolution={2}
      />
    </Container>
  );
};

export default FreeSpinButtonPlay;
